import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, PawPrint } from "lucide-react";
import { useLang } from "../LanguageContext";
import { IMAGES } from "../content";
import { Stars } from "./Stars";
import { scrollToId } from "../scroll";

const scrollTo = (id) => scrollToId(id);

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.5 + i * 0.18, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fadeUp = {
  hidden: { y: 20, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { duration: 0.7, delay: 1.0 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const slides = IMAGES.heroSlides;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, [0, 1], [1.12, 1.26]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.68]);

  const titleLines = t.hero.title.split("\n");

  return (
    <section id="home" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ scale, y }} className="absolute inset-0 will-change-transform">
        {slides.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="Restaurante São João — seafood and dining room"
            loading={i === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1400ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}
            style={{ objectPosition: "center 55%" }}
          />
        ))}
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-brown" />
      <div className="absolute inset-0 bg-gradient-to-t from-brown/70 via-transparent to-brown/30" />

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-center">
        <motion.p
          variants={fadeUp}
          custom={-2}
          initial="hidden"
          animate="visible"
          className="text-ivory/80 text-xs md:text-sm uppercase tracking-[0.35em] mb-6"
        >
          {t.hero.eyebrow}
        </motion.p>

        <h1 className="font-serif text-ivory leading-[0.95] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]">
          {titleLines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span variants={lineReveal} custom={i} initial="hidden" animate="visible" className="block">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p variants={fadeUp} custom={0} initial="hidden" animate="visible" className="mt-8 max-w-2xl font-serif italic text-ivory/90 text-lg md:text-2xl leading-relaxed">
          {t.hero.subtitle}
        </motion.p>

        <motion.div variants={fadeUp} custom={2} initial="hidden" animate="visible" className="mt-10 flex flex-wrap items-center gap-4">
          <button
            onClick={() => scrollTo("reserve")}
            data-testid="hero-reserve-button"
            className="group inline-flex items-center gap-2 bg-gold text-brown rounded-full px-9 py-[1.15rem] text-sm tracking-wide font-medium transition-[background-color,transform] duration-300 hover:bg-ivory hover:-translate-y-0.5"
          >
            {t.hero.reserve}
          </button>
          <button
            onClick={() => scrollTo("featured")}
            data-testid="hero-menu-button"
            className="inline-flex items-center gap-2 border border-ivory/50 text-ivory rounded-full px-9 py-[1.15rem] text-sm tracking-wide transition-[background-color,color,transform] duration-300 hover:bg-ivory hover:text-brown hover:-translate-y-0.5"
          >
            {t.hero.explore}
          </button>
        </motion.div>

        <motion.div
          variants={fadeUp}
          custom={3}
          initial="hidden"
          animate="visible"
          data-testid="hero-pet-friendly"
          className="mt-7 inline-flex items-center gap-3 self-start rounded-full border border-ivory/20 bg-brown/25 backdrop-blur-sm px-4 py-2.5"
        >
          <PawPrint size={16} className="text-gold shrink-0" strokeWidth={1.8} />
          <span className="text-[11px] uppercase tracking-[0.25em] text-gold">{t.heroPet.label}</span>
          <span className="hidden sm:inline text-ivory/25">·</span>
          <span className="text-sm font-light text-ivory/80">{t.heroPet.text}</span>
        </motion.div>
      </div>

      <motion.div
        variants={fadeUp}
        custom={4}
        initial="hidden"
        animate="visible"
        className="absolute bottom-8 inset-x-0 z-10 max-w-[1400px] mx-auto px-6 md:px-12 flex items-end justify-between"
      >
        <div className="flex flex-col gap-1.5" data-testid="google-rating">
          <Stars size={18} />
          <div className="flex items-baseline gap-2 text-ivory">
            <span className="font-serif text-xl leading-none">4.4</span>
            <span className="text-ivory/70 text-sm">{t.hero.ratingLabel}</span>
          </div>
          <span className="text-ivory/60 text-xs tracking-wide">{t.hero.reviewsLabel}</span>
        </div>
        <button onClick={() => scrollTo("about")} className="hidden md:flex flex-col items-center gap-2 text-ivory/70 hover:text-ivory transition-colors">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
            <ArrowDown size={16} />
          </motion.span>
        </button>
      </motion.div>
    </section>
  );
};
